import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, bbFetch, Eyebrow, authHeaders } from './bbShared';
import { LevelListDialog } from './LevelControls';
import { DeclHistoryDialog } from './DeclHistoryDialog';
import { PrevisionPonderee } from './PrevisionPonderee';
import { SceneDirection, ADebloquer, Mouvements, Activite } from './DirectionBlocks';
import { ParProfil } from './ParProfil';
import { DossiersTable } from './DossiersTable';
import { SupprimerDialog, SortirDialog, CorbeilleDialog, ParamsDialog } from './BbDialogs';

const periodLabel = (d) => {
  if (!d) return '';
  const s = new Date(`${d.start}T12:00:00`); const e = new Date(`${d.end}T12:00:00`);
  if (d.period === 'month') return s.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return `semaine du ${s.getDate()} au ${e.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
};

export default function DirectionView({ token, user }) {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('week');
  const [commercial, setCommercial] = useState('');
  const [data, setData] = useState(null);
  const [levelList, setLevelList] = useState(null);
  const [declOf, setDeclOf] = useState(null);
  const [person, setPerson] = useState(null);
  const [del, setDel] = useState(null);
  const [sortir, setSortir] = useState(null);
  const [trash, setTrash] = useState(false);
  const [params, setParams] = useState(false);
  const [relancing, setRelancing] = useState(false);

  const load = useCallback(() => {
    bbFetch(token, `/bb/direction?period=${period}${commercial ? `&commercial_id=${commercial}` : ''}`).then(setData).catch(e => toast.error(e.message));
  }, [token, period, commercial]);
  useEffect(() => { load(); }, [load]);

  const open = (id) => navigate(`/business-bouillant/${id}`);
  const take = async (id) => { try { await bbFetch(token, `/bb/dossiers/${id}/prendre`, { method: 'POST' }); toast.success('Dossier pris en charge'); load(); } catch (e) { toast.error(e.message); } };
  const relancer = async () => {
    setRelancing(true);
    try { const r = await bbFetch(token, '/bb/relancer', { method: 'POST' }); toast.success(`${r.relances} commercial(aux) relancé(s)`); } catch (e) { toast.error(e.message); } finally { setRelancing(false); }
  };
  const exportFile = async (fmt) => {
    const res = await fetch(`${API}/api/bb/export?fmt=${fmt}${commercial ? `&commercial_id=${commercial}` : ''}`, { headers: authHeaders(token) });
    if (!res.ok) { toast.error("Erreur d'export"); return; }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = url; a.download = `business_bouillant.${fmt}`; a.click(); URL.revokeObjectURL(url);
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F26A1B]" /></div>;
  const changeLevel = async (id, niveau) => {
    try { await bbFetch(token, `/bb/dossiers/${id}`, { method: 'PATCH', body: JSON.stringify({ niveau }) }); toast.success('Température mise à jour'); load(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5 bg-[#F3F2EE] -mx-4 sm:-mx-6 px-4 sm:px-6 py-6 rounded-2xl" data-testid="bb-direction">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Eyebrow>Business commercial · Vue Direction</Eyebrow>
          <h1 className="text-4xl sm:text-5xl font-bold uppercase leading-none mt-1" style={{ fontFamily: 'var(--font-display)' }}>Business Commercial</h1>
          <p className="text-sm text-slate-500 mt-2">Toutes les simulations CAPEX en cours remontent automatiquement (Tiède par défaut) et sont classées selon leur température.</p>
        </div>
        <div className="flex gap-2">
          {user?.role === 'super_admin' && <button type="button" onClick={() => setParams(true)} className="inline-flex w-11 h-11 items-center justify-center rounded-lg border border-[#D9D6CF] bg-white hover:bg-[#EEECE8]" aria-label="Paramètres" data-testid="bb-params-btn"><Settings2 className="w-4 h-4" /></button>}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Eyebrow>Période analysée · {periodLabel(data)}</Eyebrow>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">Commercial</span>
          <select value={commercial} onChange={e => setCommercial(e.target.value)} className="rounded-lg border border-[#D9D6CF] bg-white px-3 py-2 text-sm" data-testid="bb-filter-commercial">
            <option value="">Toute l'équipe</option>{data.commerciaux.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="flex rounded-lg bg-[#E5E3DE] p-1" role="tablist">
            {[['week', 'Semaine'], ['month', 'Mois']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setPeriod(k)} data-testid={`bb-period-${k}`}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${period === k ? 'bg-[#25272B] text-white' : 'text-slate-600'}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      <SceneDirection scene={data.scene} period={period} onPickLevel={setLevelList} />
      <PrevisionPonderee prevision={data.prevision} />
      <ADebloquer items={data.a_debloquer} onTake={take} onOpen={open} />
      <ParProfil rows={data.par_commercial} params={data.params} period={period} onDetail={setPerson} />
      <DossiersTable dossiers={data.dossiers} onOpen={open} onLevel={changeLevel} onDelete={setDel} onExport={exportFile} corbeilleCount={data.corbeille_count} onCorbeille={() => setTrash(true)} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Mouvements items={data.mouvements} period={period} />
        <Activite rows={data.activite} onRelancer={relancer} relancing={relancing} onDetail={setDeclOf} />
      </div>
      <DeclHistoryDialog commercialId={declOf} token={token} onClose={() => { setDeclOf(null); load(); }} />
      <LevelListDialog level={levelList} person={person} dossiers={data.dossiers} onClose={() => { setLevelList(null); setPerson(null); }} onOpen={open} />
      <SupprimerDialog open={!!del} dossier={del} token={token} onClose={() => setDel(null)} onDone={() => { setDel(null); load(); }} onSortir={() => { setSortir(del); setDel(null); }} />
      <SortirDialog open={!!sortir} dossier={sortir} token={token} onClose={() => setSortir(null)} onDone={() => { setSortir(null); load(); }} />
      <CorbeilleDialog open={trash} token={token} isSuperAdmin={user?.role === 'super_admin'} onClose={() => setTrash(false)} onChanged={load} />
      <ParamsDialog open={params} token={token} onClose={() => setParams(false)} onSaved={load} />
    </div>
  );
}
